import { Button, ButtonGroup } from "@components/button";
import {
    ActionType,
    addEmptyLineItem,
    addNewLineItem,
    ISmartFastEntriesActionEvent
} from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";
import { getNewItemsMaxId } from "@odata/Data.utils";
import {
    EntitySetName,
    IPrTaxDiscountTypeEntity,
    IPrTaxpayerDeclarationChildEntity,
    IPrTaxpayerDeclarationDiscountEntity,
    IPrTaxpayerDeclarationEntity,
    PrOtherProviderAddressEntity,
    PrOtherProviderEmployerAddressEntity,
    PrTaxpayerDeclarationChildEntity,
    PrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationOtherProviderEntity
} from "@odata/GeneratedEntityTypes";
import {
    PrChildDeductionOrderCode,
    PrTaxDiscountTypeCode,
    PrTaxpayerDeclarationStatusCode
} from "@odata/GeneratedEnums";
import { setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import {
    disabilityDiscountType,
    getPathsForDeductionType,
    hasDisabilityDiscount,
    isThereOtherProviderPath,
    massExportToPDF,
    showChildrenPath
} from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.utils";
import { ICopyEntityArgs } from "@utils/DraftUtils";
import { isNotDefined, isObjectEmpty } from "@utils/general";
import { logger } from "@utils/log";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import { FieldType } from "../../../enums";
import { getUtcDate } from "../../../types/Date";
import { IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import { isMassExportAction } from "../../../views/table/TableToolbar.utils";

export interface ITaxPayerDeclarationCustomData extends IFormStorageDefaultCustomData {
    discountTypes?: Record<string, IPrTaxDiscountTypeEntity>;
}

interface IProps extends IFormViewProps<IPrTaxpayerDeclarationEntity, ITaxPayerDeclarationCustomData> {
}

class TaxpayerDeclarationFormView extends FormViewForExtend<IPrTaxpayerDeclarationEntity, IProps> {
    loadPrTaxDiscountTypes = async (): Promise<void> => {
        const res = await this.props.storage.oData.getEntitySetWrapper(EntitySetName.PrTaxDiscountTypes)
                .query()
                .fetchData<IPrTaxDiscountTypeEntity[]>();

        const discountTypes: Record<string, IPrTaxDiscountTypeEntity> = {};

        res.value?.forEach(discountType => {
            discountTypes[discountType.Code] = discountType;
        });

        this.props.storage.setCustomData({
            discountTypes
        });

        this.props.storage.refresh();
    };

    getAdditionalLoadPromise = () => {
        return [
            this.loadPrTaxDiscountTypes()
        ];
    };

    async handleCustomHeaderAction(actionId: string): Promise<void> {
        const { storage } = this.props;
        if (isMassExportAction(actionId)) {
            await massExportToPDF([this.props.storage.data.bindingContext.getKey()?.toString()], storage.context);
        }

        return super.handleCustomHeaderAction(actionId);
    }

    handleMasterDataChange = (): void => {
        const storage = this.props.storage;
        storage.setValueByPath(isThereOtherProviderPath, !!this.entity.OtherProvider.PersonalIdentificationNumber);
        this.fillLocalContextFieldsFromDiscounts();

        const hasChildren = !!this.entity.Children?.length;
        storage.setValueByPath(showChildrenPath, hasChildren);
    }

    async onAfterLoad() {
        const storage = this.props.storage;

        if (storage.data.bindingContext.isNew()) {
            if (this.props.formProps?.defaultData?.Employee) {
                this.entity.Employee = this.props.formProps.defaultData.Employee;
                await this.fillDataFromLastEmployeeDeclaration(this.entity.Employee.Id);
            }
            await setMatchingNumberRange(storage, true);
            this.entity.Discounts = [
                this.getNewDiscountItem(PrTaxDiscountTypeCode.TaxPayerDiscount)
            ];
        }
        {
            const basicDiscount = this.entity.Discounts?.find(d => d.DiscountTypeCode === PrTaxDiscountTypeCode.BasicDisabilityDiscount);
            const extendedDiscount = this.entity.Discounts?.find(d => d.DiscountTypeCode === PrTaxDiscountTypeCode.ExtendedDisabilityDiscount);
            if (!isObjectEmpty(basicDiscount) || !isObjectEmpty(extendedDiscount)) {
                storage.setValueByPath(hasDisabilityDiscount, true);
                storage.setValueByPath(disabilityDiscountType, !isObjectEmpty(basicDiscount) ? PrTaxDiscountTypeCode.BasicDisabilityDiscount : PrTaxDiscountTypeCode.ExtendedDisabilityDiscount);
            }
        }

        storage.data.disabled = this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.WaitingForSignature;

        this.handleMasterDataChange();

        return super.onAfterLoad();
    }

    getNewDiscountItem = (typeCode: PrTaxDiscountTypeCode) => {
        const storage = this.props.storage;
        const newItemsMax = getNewItemsMaxId(this.entity.Discounts ?? []);

        const newItem = addEmptyLineItem<IPrTaxpayerDeclarationDiscountEntity>({
            storage,
            bindingContext: storage.data.bindingContext.navigate(PrTaxpayerDeclarationEntity.Discounts),
            newItemsCount: newItemsMax + 1,
            context: storage.context,
            columns: []
        });

        newItem.DiscountTypeCode = typeCode;
        return newItem;
    }

    fillDataFromLastEmployeeDeclaration = async (employeeId: number): Promise<void> => {
        const storage = this.props.storage;
        try {
            const res = await storage.oData.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations)
                    .query()
                    .expand(PrTaxpayerDeclarationEntity.OtherProvider, query => {
                        return query.expand(PrTaxpayerDeclarationOtherProviderEntity.Address, q2 => q2.expand(PrOtherProviderAddressEntity.Country))
                                .expand(PrTaxpayerDeclarationOtherProviderEntity.EmployerAddress, q2 => q2.expand(PrOtherProviderEmployerAddressEntity.Country));
                    })
                    .expand(PrTaxpayerDeclarationEntity.Discounts)
                    .expand(PrTaxpayerDeclarationEntity.Children, query => query.expand(PrTaxpayerDeclarationChildEntity.Order))
                    .filter(`Employee/Id eq ${employeeId} AND StatusCode eq '${PrTaxpayerDeclarationStatusCode.Signed}'`)
                    .orderBy("Year", false)
                    .top(1)
                    .fetchData<IPrTaxpayerDeclarationEntity[]>();

            if (res.value?.length) {
                const copy = this.createEntityCopy({ entity: res.value[0] });
                storage.data.entity = {
                    ...this.entity,
                    Discounts: copy.Discounts,
                    Children: copy.Children,
                    OtherProvider: copy.OtherProvider
                };
                this.handleMasterDataChange();
                storage.refresh();
            }
        } catch (e) {
            logger.error("Couldn't fetch last taxpayer declaration", e);
            return null;
        }
    }

    handleEmployeeChange = async (e: ISmartFieldChange): Promise<void> => {
        const storage = this.props.storage;
        const path = e.bindingContext.getPath();
        const employeeId = e.value;

        if (path === PrTaxpayerDeclarationEntity.Employee && storage.data.bindingContext.isNew() && e.triggerAdditionalTasks) {
            await this.fillDataFromLastEmployeeDeclaration(Number(employeeId));
        }
    };

    handleChange = (e: ISmartFieldChange): void => {
        this.props.storage.handleChange(e);

        if (e.bindingContext.getPath() === showChildrenPath) {
            if (!e.value) {
                this.entity.Children = [];
            } else {
                addNewLineItem(this.props.storage, PrTaxpayerDeclarationEntity.Children, PrTaxpayerDeclarationEntity.Children, [], {
                    [PrTaxpayerDeclarationChildEntity.OrderCode]: PrChildDeductionOrderCode._1stChild,
                    [PrTaxpayerDeclarationChildEntity.Order]: {
                        Code: PrChildDeductionOrderCode._1stChild
                    },
                    [PrTaxpayerDeclarationChildEntity.IsDisabilityDiscount]: false
                });
            }
        }
        this.handleEmployeeChange(e);
        if (e.type === FieldType.Switch) {
            this.props.storage.refresh();
        } else {
            this.props.storage.refreshFields();
        }
    };

    handleSignClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Signed;
        this.save({ data });
    };

    handleSendToSignClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.WaitingForSignature;
        this.save({ data });
    };

    handleSaveDraftClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Draft;
        this.save({ data });
    };

    handleInvalidateClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Ended;
        this.save({ data });
    };

    handleEditClick = (): void => {
        const data = this.onBeforeSave();
        data.StatusCode = PrTaxpayerDeclarationStatusCode.Draft;
        this.save({ data });
    };

    createEntityCopy(args: ICopyEntityArgs = {}): IPrTaxpayerDeclarationEntity {
        const copy = cloneDeep(args.entity ?? this.entity);
        const deleteProps = [PrTaxpayerDeclarationEntity.Id, PrTaxpayerDeclarationEntity.StatusCode, PrTaxpayerDeclarationEntity.Status,
            PrTaxpayerDeclarationEntity.DateSigned, PrTaxpayerDeclarationEntity.DateEnded, PrTaxpayerDeclarationEntity.NumberOurs];
        for (const prop of deleteProps) {
            delete copy[prop];
        }
        return copy;
    }

    renderButtons = (): ReactElement => {
        const storage = this.props.storage;
        const isFormReadOnly = storage.isReadOnly;

        if (this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.Signed) {
            return <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCancel"}
                        isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                        onClick={this.handleFormCancel}
                        isTransparent>{storage.t("Common:General.Cancel")}</Button>
                <Button hotspotId={"formSave"}
                        onClick={this.handleInvalidateClick}>
                    {storage.t("TaxpayerDeclaration:Form.Invalidate")}
                </Button>
            </ButtonGroup>;
        }


        if (this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.Ended) {
            return <ButtonGroup wrap={"wrap"}>
                <Button hotspotId={"formCopy"}
                        onClick={this.handleCopy}
                        isTransparent>{storage.t("Common:Form.CreateCopy")}</Button>
                <Button hotspotId={"formCancel"}
                        onClick={this.handleFormCancel}
                >{storage.t("Common:General.Cancel")}</Button>
            </ButtonGroup>;
        }

        return (
                <ButtonGroup wrap={"wrap"}>
                    <Button hotspotId={"formCancel"}
                            isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                            onClick={this.handleFormCancel}
                            isTransparent>{storage.t("Common:General.Cancel")}</Button>
                    {this.isDeletable &&
                            <SmartFormDeleteButton storage={storage}
                                                   onClick={this.handleDelete}
                                                   key={storage.data.uuid}/>
                    }
                    {this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.WaitingForSignature &&
                            <Button hotspotId={"edit"}
                                    onClick={this.handleEditClick}
                                    isTransparent>
                                {storage.t("Common:General.Edit")}
                            </Button>}

                    {!isFormReadOnly && <>
                        {this.entity.StatusCode !== PrTaxpayerDeclarationStatusCode.WaitingForSignature &&
                                <Button hotspotId={"saveDraft"}
                                        onClick={this.handleSaveDraftClick}
                                        isTransparent
                                        isDisabled={this.shouldDisableSaveButton()}>
                                    {storage.t("TaxpayerDeclaration:Form.Save")}
                                </Button>}
                        <Button onClick={this.handleSignClick}
                                isTransparent>
                            {storage.t("TaxpayerDeclaration:Form.Sign")}
                        </Button>
                        <Button hotspotId={"formSave"}
                                onClick={this.handleSendToSignClick}
                                isDisabled={this.shouldDisableSaveButton() || this.entity.StatusCode === PrTaxpayerDeclarationStatusCode.WaitingForSignature}>
                            {storage.t("TaxpayerDeclaration:Form.SendToSign")}
                        </Button>
                    </>}
                </ButtonGroup>
        );
    };

    fillLocalContextFieldsFromDiscounts = (): void => {
        this.entity.Discounts?.forEach(discount => {
            const {
                switchPath
            } = getPathsForDeductionType(discount.DiscountTypeCode as PrTaxDiscountTypeCode);

            this.props.storage.setValueByPath(switchPath, true);
        });
    }

    createDiscountsFromLocalContext = (): IPrTaxpayerDeclarationDiscountEntity[] => {
        const discounts: IPrTaxpayerDeclarationDiscountEntity[] = [];
        const storage = this.props.storage;

        Object.values(PrTaxDiscountTypeCode).filter(type => {
            // Exclude disability discounts as they are handled separately
            return type !== PrTaxDiscountTypeCode.BasicDisabilityDiscount && type !== PrTaxDiscountTypeCode.ExtendedDisabilityDiscount;
        }).forEach(type => {
            const { switchPath } = getPathsForDeductionType(type);

            if (storage.getValueByPath(switchPath)) {
                const discount = {
                    DiscountTypeCode: type
                } as IPrTaxpayerDeclarationDiscountEntity;

                discounts.push(discount);
            }
        });

        if (storage.getValueByPath(hasDisabilityDiscount)) {
            const discountType = storage.getValueByPath(disabilityDiscountType);
            const discount = {
                DiscountTypeCode: discountType
            } as IPrTaxpayerDeclarationDiscountEntity;

            discounts.push(discount);
        }

        storage.data.origEntity?.Discounts?.forEach(origDiscount => {
            const matchingDiscount = discounts.find(discount => discount.DiscountTypeCode === origDiscount.DiscountTypeCode);
            if (matchingDiscount) {
                matchingDiscount.Id = origDiscount.Id;
            }
        });

        return discounts;
    };

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent): void => {
        this.props.storage.handleLineItemsAction(args);

        if (args.actionType === ActionType.Add) {
            const [item] = args.affectedItems;
            const newChild = (item as IPrTaxpayerDeclarationChildEntity);
            newChild.IsDisabilityDiscount = false;
            const childrenCount = this.entity.Children?.length ?? 0;
            newChild.OrderCode = childrenCount === 0 ? PrChildDeductionOrderCode._1stChild : childrenCount > 2 ? PrChildDeductionOrderCode._3rdChild : PrChildDeductionOrderCode._2ndChild;
            newChild.Order = {
                Code: newChild.OrderCode as PrChildDeductionOrderCode
            };
        }

        this.props.storage.refresh();
    };

    onBeforeSave(): IPrTaxpayerDeclarationEntity {
        const storage = this.props.storage;
        const entity = cloneDeep(this.entity);

        if (isNotDefined(entity.Year)) {
            storage.setValueByPath(PrTaxpayerDeclarationEntity.Year, getUtcDate().getFullYear());
        }

        storage.clearEmptyLineItems(PrTaxpayerDeclarationEntity.Children, true);
        entity.Discounts = this.createDiscountsFromLocalContext();

        if (!storage.getValueByPath(isThereOtherProviderPath)) {
            entity.OtherProvider = null;
        }
        if (!this.entity.OtherProvider?.IsEmployed && !isObjectEmpty(entity.OtherProvider)) {
            delete entity.OtherProvider?.EmployerAddress;
        }


        return entity;
    }
}

export default withPermissionContext(TaxpayerDeclarationFormView);